import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { ContainerStepOne, BtnWrapper, PromoCodeContainer } from './PromoSignUp.style';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import { LandingPage } from 'src/domains/players/state/specialEventsState/LandingPage';
import { action, makeObservable, observable } from 'mobx';
import { SpecialEvents } from 'src/domains/players/state/specialEventsState/SpecialEvents';
import { PromoCodesState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/stepsStructure/createAccount/promoCodesState/PromoCodes.state';
import { Common, useCommon } from 'src/domains/common/Common';
import { LoginMessages } from 'src/domains/players/webview/components/SignUp/signupPrimary/SignUp.style';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { DateTime } from 'src_common/utils/time/time';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';

interface PropsType {
    promotionSlug: string;
    lpState: LandingPage;
}

export class PromoSignupState {
    @observable public submitErrors: string | null = null;
    private readonly promoCodesState: PromoCodesState;
    private readonly googleTagManager: GoogleTagManagerState;

    public constructor(
        private readonly specialEvents: SpecialEvents,
        private readonly lpState: LandingPage,
        private readonly promotionSlug: string,
        private readonly promoCode: string,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.googleTagManager = GoogleTagManagerState.get(common);
        this.promoCodesState = PromoCodesState.get(this.common);
    }

    @action private validatePromoCode = async (): Promise<void> => {
        this.submitErrors = null;
        if (this.promoCode === '') return;

        const promoCodeResponse = await this.promoCodesState.checkPromoCode(this.promoCode);

        if (promoCodeResponse.correct === false) {
            this.submitErrors = promoCodeResponse.message;
            return;
        }
    };

    @action public redirectToSignup = async (): Promise<void> => {
        await this.validatePromoCode();

        if (this.submitErrors !== null) return;

        this.lpState.addToLocalStore();
        const date = DateTime.current().utc().format('YYYY-MM-DD HH:mm:ss [GMT]');

        this.googleTagManager.gtmLandingPageToSignUp(this.promotionSlug, date);
        this.specialEvents.handleRedirectToNewSignup(this.promotionSlug);
    };
}

export const PromoSignUpStep = observer('PromoSignUpStep', ({ lpState, promotionSlug }: PropsType) => {
    const common = useCommon();
    const specialEvents = SpecialEvents.get(common);
    const state = specialEvents.promoSignUpData;
    const { promoCode, ctaText } = specialEvents.getLandingPageState(promotionSlug);
    const languagesState = LanguagesState.get(common);
    const [promoSignupState] = useState(
        () => new PromoSignupState(specialEvents, lpState, promotionSlug, promoCode, common)
    );

    const emailPlaceholder = languagesState.getTranslation('promo-sidebar.signup.placeholder.email', '*Email address');
    const passwordPlaceholder = languagesState.getTranslation(
        'promo-sidebar.signup.placeholder.password',
        '*Create password'
    );

    if (state === null) {
        return null;
    }

    return (
        <ContainerStepOne>
            <Input
                state={state.welcomeState.emailState}
                placeholder={emailPlaceholder}
                placeholderSize='small'
                showSuccessIcon={state.welcomeState.isCorrectEmail}
                dataTest='landing-page-email-input'
            />

            <Input
                placeholder={passwordPlaceholder}
                placeholderSize='small'
                state={state.welcomeState.passwordState}
                type={state.welcomeState.isShowPassword === true ? 'text' : 'password'}
                showPassword={state.welcomeState.showPassword}
                isShowPassword={state.welcomeState.isShowPassword}
                showPasswordFlag={true}
                showSuccessIcon={state.welcomeState.isCorrectPassword}
                dataTest='landing-page-password-input'
            />

            <PromoCodeContainer data-test='landing-page-promo-code'>{promoCode}</PromoCodeContainer>
            {promoSignupState.submitErrors === null ? null : (
                <LoginMessages
                    type='error'
                    message={promoSignupState.submitErrors}
                />
            )}

            <BtnWrapper
                disabled={state.welcomeState.stepIsValid === false}
                type='submit'
                size='large'
                onClick={promoSignupState.redirectToSignup}
                dataTest='landing-page-signup-button'
            >
                {ctaText === '' ? 'Continue' : ctaText}
            </BtnWrapper>
        </ContainerStepOne>
    );
});
